import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import bkstApi from "../../api";
import RadioCardGroup from "../../common/component/RadioCardGroup";

const AVAILABILITY_OPTIONS = [
  {
    value: "activate",
    title: "Activate",
    description: "Make selected items available for sale",
  },
  {
    value: "deactivate",
    title: "Deactivate",
    description: "Move selected items to draft",
  },
];

export default function BulkAvailabilityDialog({
  open,
  selectedIds,
  currentStatus,
  onClose,
  onProductsUpdate,
}) {
  // Selected items already share the status of the tab they're listed under,
  // so the matching action is a no-op & the other one is pre-selected
  const unavailableAction =
    currentStatus === "ACTIVE" ? "activate" : currentStatus === "DRAFT" ? "deactivate" : "";
  const defaultAction =
    unavailableAction === "activate"
      ? "deactivate"
      : unavailableAction === "deactivate"
      ? "activate"
      : "";

  const [action, setAction] = useState(defaultAction);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAction(defaultAction);
      setSaving(false);
    }
  }, [open, defaultAction]);

  const options = AVAILABILITY_OPTIONS.map((opt) =>
    opt.value === unavailableAction
      ? {
          ...opt,
          disabled: true,
          description: `Selected items are already ${
            currentStatus === "ACTIVE" ? "available for sale" : "in draft"
          }`,
        }
      : opt
  );

  const handleSave = () => {
    if (!action || action === unavailableAction || !selectedIds?.length) return;

    const newStatus = action === "activate" ? "ACTIVE" : "DRAFT";
    setSaving(true);

    bkstApi
      .put(`/my/products/status`, { ids: selectedIds, status: newStatus })
      .then(() => {
        localStorage.removeItem("products");
        onProductsUpdate(selectedIds.map((id) => ({ id, updates: { status: newStatus } })));
        onClose();
      })
      .catch((error) => {
        console.error("Error updating availability:", error);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const selectedCount = selectedIds?.length || 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", pr: 6 }}>
        <Typography variant="h6">Change Availability</Typography>
        <Typography variant="body2" color="textSecondary">
          {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          <RadioCardGroup
            name="availability"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            options={options}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !action}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
      {saving && (
        <Backdrop open={true} sx={{ zIndex: 2000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Dialog>
  );
}
